import {FC, MouseEvent, useState} from 'react';
import {AccountBalanceOutlined} from '@mui/icons-material';
import {Box, Chip, Popover} from '@mui/material';
import {FormattedMessage, useIntl} from 'react-intl';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetIcons} from 'src/components/mobile/ActionSheet';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {UserBexioDataDTO} from 'src/types/users';
import {TableCard} from 'src/components/tables/respnsiveTable/TableCard';
import {MenuButton} from 'src/components/tables/respnsiveTable/MenuButton';
import {BexioAccountActions} from '../BexioAccountActions';
import {popoverSx} from './BexioAccountItemCard.sx';

type BexioAccountItemCardProps = {
    account: UserBexioDataDTO;
    onUnlink: (bexioAccountId: number) => void;
    onMakePrimary: (bexioAccountId: number) => void;
    onUpdate: (bexioAccountId: number) => void;
};

/**
 * The card form of a linked Bexio account.
 *
 * The menu holds the row's own actions component, handed the same three callbacks the table hands
 * it, so unlinking, making primary and editing the Bexio id remain one implementation.
 */
export const BexioAccountItemCard: FC<BexioAccountItemCardProps> = ({
                                                                        account,
                                                                        onUnlink,
                                                                        onMakePrimary,
                                                                        onUpdate,
                                                                    }) => {
    const intl = useIntl();
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // One node, drawn by whichever of the two the width calls for: the three callbacks behind it
    // are the same in both.
    const accountActions = (
        <BexioAccountActions
            bexioAccountId={account.bexioAccountId}
            isPrimary={account.primary}
            onUnlink={onUnlink}
            onMakePrimary={onMakePrimary}
            onUpdate={onUpdate}
        />
    );

    // The card's one menu, in the head rather than absolutely placed over the corner of the card.
    const menu = (
        <>
            <MenuButton handleClick={handleClick}/>
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<AccountBalanceOutlined/>}
                    title={account.companyName ?? account.bexioAccountId}
                    subtitle={account.bexioId}
                >
                    <ActionSheetIcons>{accountActions}</ActionSheetIcons>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={popoverSx}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    {accountActions}
                </Popover>
            )}
        </>
    );

    return (
        <TableCard>
            <PhoneCardHead
                icon={<AccountBalanceOutlined/>}
                title={account.companyName ?? account.bexioAccountId}
                action={menu}
            />
            {/* Only the one account that is primary says so; the others said "-" in a row of
                their own, which is a column's habit rather than a card's. */}
            {account.primary && (
                <Box sx={phonePillsRowSx}>
                    <Chip
                        label={intl.formatMessage({id: 'primary'})}
                        size="small"
                        color="success"
                        variant="filled"
                    />
                </Box>
            )}
            <PhoneCardFacts
                facts={[
                    {
                        label: <FormattedMessage id="bexioAccountId"/>,
                        value: account.bexioAccountId ?? '-',
                    },
                    {
                        label: <FormattedMessage id="bexioId"/>,
                        value: account.bexioId ?? '-',
                    },
                ]}
            />
        </TableCard>
    );
};
